import { primary } from '../../../theme/palette'
import { useMemo, useRef, useState } from 'react'
import {
  Avatar, AvatarGroup, Box, Button, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  IconButton, List, ListItemAvatar, ListItemButton, ListItemText,
  Menu, MenuItem, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined'
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined'
import { AnimatePresence, motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Team, TeamMember, TeamMemberPreview, User } from '@cmms/types'

import { api } from '../../../lib/api'
import { toast } from '../../../store/snackbarStore'
import { UserAvatar } from '../../../components/UserAvatar'
import { useConfirm } from '../../../components/ConfirmDialog'
import { usePermissions } from '../../../hooks/usePermissions'
import { useOrgSettings } from '../../../hooks/useOrgSettings'
import { CustomerScopePicker } from '../../../components/CustomerScopePicker'
import { CustomerScopeChip } from '../../../components/CustomerScopeChip'

const TEAM_COLORS = ['#0D9488', '#D97706', '#0E7490', '#EA580C', '#059669', '#7C3AED', '#DB2777', '#2563EB']

// Same map/formatter as MemberList.tsx/MemberDetail.tsx/PeoplePage.tsx —
// kept as a local copy here too rather than a shared import, matching
// how those three already duplicate it.
const ROLE_COLORS: Record<string, string> = {
  org_admin:  '#0D9488',
  admin:      '#0D9488',
  manager:    '#059669',
  technician: '#D97706',
  viewer:     '#64748B',
}
function formatRole(role: string): string {
  return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

interface Props {
  teams: Team[]
  externalCreateOpen?: boolean
  onExternalCreateClose?: () => void
}

export function TeamsView({ teams, externalCreateOpen, onExternalCreateClose }: Props) {
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null)
  const [internalCreateOpen, setInternalCreateOpen] = useState(false)
  const createDialogOpen = internalCreateOpen || (externalCreateOpen ?? false)

  const selectedTeam = selectedTeamId ? teams.find((t) => t.id === selectedTeamId) : null

  return (
    <>
      <AnimatePresence mode="wait">
        {selectedTeam ? (
          <motion.div
            key="detail"
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <TeamDetail team={selectedTeam} colorIndex={teams.indexOf(selectedTeam)} onBack={() => setSelectedTeamId(null)} />
          </motion.div>
        ) : (
          <motion.div
            key="grid"
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
          >
            <TeamGrid
              teams={teams}
              onSelect={setSelectedTeamId}
              onCreateClick={() => setInternalCreateOpen(true)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <CreateTeamDialog open={createDialogOpen} onClose={() => { setInternalCreateOpen(false); onExternalCreateClose?.() }} />
    </>
  )
}

// ─── Team grid ──────────────────────────────────────────────────────────────

function TeamGrid({
  teams, onSelect, onCreateClick,
}: {
  teams: Team[]
  onSelect: (id: string) => void
  onCreateClick: () => void
}) {
  const { can } = usePermissions()
  const confirmDlg = useConfirm()

  // Per-card kebab menu
  const [menuState, setMenuState] = useState<{ anchor: HTMLElement; team: Team } | null>(null)
  const qc = useQueryClient()

  const deleteM = useMutation({
    mutationFn: (id: string) => api.teams.remove(id),
    onSuccess: () => { toast.success('Team deleted'); qc.invalidateQueries({ queryKey: ['teams'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  return (
    <Box
      sx={{
        flex: 1, minHeight: 0, overflowY: 'auto', p: 3,
        scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
        '&::-webkit-scrollbar': { width: 8 },
        '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
        '&:hover::-webkit-scrollbar-thumb': {
          background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)',
        },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          gap: 2,
        }}
      >
        {teams.map((t, i) => {
          const color = TEAM_COLORS[i % TEAM_COLORS.length]!
          const preview: TeamMemberPreview[] = Array.isArray(t.members_preview) ? t.members_preview : []
          const overflow = t.member_count - preview.length
          return (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04, duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
            >
              <Box
                onClick={() => onSelect(t.id)}
                sx={{
                  position: 'relative', borderRadius: 3.5, height: '100%', minHeight: 200,
                  bgcolor: 'background.paper', cursor: 'pointer', overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                  border: '1px solid', borderColor: 'transparent',
                  boxShadow: (th) => th.palette.mode === 'light'
                    ? '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)'
                    : '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
                  transition: 'transform 240ms cubic-bezier(0.34,1.56,0.64,1), box-shadow 240ms ease, border-color 200ms ease',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.01)',
                    borderColor: `${color}40`,
                    boxShadow: (th) => th.palette.mode === 'light'
                      ? `0 16px 40px -12px ${color}22, 0 4px 12px rgba(0,0,0,0.06)`
                      : `0 16px 40px -12px ${color}44, 0 4px 12px rgba(0,0,0,0.3)`,
                    '& .team-icon-ring': { transform: 'scale(1.08)' },
                    '& .team-arrow': { opacity: 1, transform: 'translateX(0)' },
                  },
                }}
              >
                {/* Gradient background glow */}
                <Box sx={{
                  position: 'absolute', top: -40, right: -40, width: 140, height: 140,
                  borderRadius: '50%', pointerEvents: 'none',
                  background: `radial-gradient(circle, ${color}10 0%, transparent 70%)`,
                }} />

                <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
                  {/* Top row: icon + kebab */}
                  <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box
                      className="team-icon-ring"
                      sx={{
                        width: 48, height: 48, borderRadius: '50%',
                        overflow: 'hidden',
                        boxShadow: `0 4px 14px -4px ${color}50`,
                        transition: 'transform 240ms cubic-bezier(0.34,1.56,0.64,1)',
                      }}
                    >
                      {t.avatar_url ? (
                        <Avatar src={t.avatar_url} sx={{ width: 48, height: 48 }} />
                      ) : (
                        <Box sx={{
                          width: 48, height: 48,
                          background: `linear-gradient(135deg, ${color}, ${color}BB)`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <GroupsOutlinedIcon sx={{ fontSize: 22, color: '#fff' }} />
                        </Box>
                      )}
                    </Box>
                    <IconButton size="small"
                      onClick={(e) => { e.stopPropagation(); setMenuState({ anchor: e.currentTarget, team: t }) }}
                      sx={{
                        color: 'text.disabled', mt: -0.25,
                        '&:hover': { color, bgcolor: `${color}0A` },
                      }}>
                      <MoreHorizIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>

                  {/* Team name + description */}
                  <Typography sx={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.25, mb: 0.5 }} noWrap>
                    {t.name}
                  </Typography>
                  {t.description ? (
                    <Typography sx={{
                      fontSize: 12.5, color: 'text.secondary', lineHeight: 1.45,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                    }}>
                      {t.description}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: 12.5, color: 'text.disabled', fontStyle: 'italic' }}>No description</Typography>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    <CustomerScopeChip customerId={t.customer_id} requiredNullLabel="Auto (by members)" />
                  </Box>

                  {/* Spacer */}
                  <Box sx={{ flex: 1 }} />

                  {/* Footer: avatars + member count + arrow */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2.5 }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      {preview.length > 0 ? (
                        <AvatarGroup max={4} sx={{
                          '& .MuiAvatar-root': {
                            width: 30, height: 30, fontSize: 10, fontWeight: 700,
                            borderWidth: 2, borderColor: 'background.paper',
                          },
                        }}>
                          {preview.slice(0, 4).map((p) => (
                            <UserAvatar key={p.id} name={p.name} avatarUrl={p.avatar_url} size={30} sx={{ bgcolor: color }} />
                          ))}
                          {overflow > 0 && (
                            <Avatar sx={{ width: 30, height: 30, fontSize: 10, fontWeight: 700, bgcolor: `${color}14`, color }}>
                              +{overflow}
                            </Avatar>
                          )}
                        </AvatarGroup>
                      ) : (
                        <Box sx={{
                          width: 30, height: 30, borderRadius: '50%', border: '2px dashed',
                          borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <PersonAddOutlinedIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                        </Box>
                      )}
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 600 }}>
                        {t.member_count} member{t.member_count === 1 ? '' : 's'}
                      </Typography>
                    </Stack>
                    <Box
                      className="team-arrow"
                      sx={{
                        width: 28, height: 28, borderRadius: '50%',
                        bgcolor: `${color}10`, color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        opacity: 0, transform: 'translateX(-6px)',
                        transition: 'opacity 200ms ease, transform 200ms ease',
                      }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>&rsaquo;</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </motion.div>
          )
        })}

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: teams.length * 0.04, duration: 0.32, ease: [0.32, 0.72, 0, 1] }}
        >
          <Tooltip title={can('teams:create') ? '' : 'You don\'t have permission'} arrow>
            <Box
              onClick={can('teams:create') ? onCreateClick : undefined}
              sx={{
                borderRadius: 3.5, border: '2px dashed', borderColor: 'divider',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                height: '100%', minHeight: 200,
                cursor: can('teams:create') ? 'pointer' : 'not-allowed',
                opacity: can('teams:create') ? 1 : 0.45,
                transition: 'border-color 240ms ease, background-color 240ms ease, transform 240ms cubic-bezier(0.34,1.56,0.64,1)',
                '&:hover': can('teams:create') ? {
                  borderColor: 'primary.main',
                  bgcolor: (t) => `${t.palette.primary.main}06`,
                  transform: 'translateY(-4px) scale(1.01)',
                  '& .create-icon': { transform: 'rotate(90deg)' },
                } : {},
              }}
            >
              <Box
                className="create-icon"
                sx={{
                  width: 48, height: 48, borderRadius: '50%',
                  bgcolor: 'action.hover',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
                  transition: 'transform 300ms cubic-bezier(0.34,1.56,0.64,1)',
                }}
              >
                <AddIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
              </Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.secondary' }}>Create a team</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 0.5 }}>Group people to assign work together</Typography>
            </Box>
          </Tooltip>
        </motion.div>
      </Box>

      {/* Kebab menu for team cards */}
      <Menu
        anchorEl={menuState?.anchor}
        open={Boolean(menuState)}
        onClose={() => setMenuState(null)}
        slotProps={{ paper: { sx: { minWidth: 180, borderRadius: 1.5 } } }}
      >
        <MenuItem onClick={() => { onSelect(menuState!.team.id); setMenuState(null) }} sx={{ fontSize: 13 }}>
          <EditOutlinedIcon sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }} />
          View & edit
        </MenuItem>
        {can('teams:delete') && (
          <MenuItem
            onClick={() => {
              confirmDlg({ title: `Delete "${menuState!.team.name}"?`, message: "All team data will be removed.", confirmLabel: "Delete", variant: "danger" }).then((ok) => ok && deleteM.mutate(menuState!.team.id))
              setMenuState(null)
            }}
            sx={{ fontSize: 13, color: 'error.main' }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 16, mr: 1.5 }} />
            Delete team
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}

// ─── Team detail ────────────────────────────────────────────────────────────

function TeamDetail({ team, colorIndex, onBack }: { team: Team; colorIndex: number; onBack: () => void }) {
  const qc = useQueryClient()
  const { can } = usePermissions()
  const confirmDlg = useConfirm()
  const { formatDate, formatDateShort } = useOrgSettings()
  const color = TEAM_COLORS[colorIndex % TEAM_COLORS.length]!

  const [addMembersOpen, setAddMembersOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editName, setEditName] = useState(team.name)
  const [editDesc, setEditDesc] = useState(team.description ?? '')
  const [editCustomerId, setEditCustomerId] = useState<string | null>(team.customer_id)
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null)
  const avatarInputRef = useRef<HTMLInputElement>(null)

  const uploadAvatarM = useMutation({
    mutationFn: (file: File) => api.teams.uploadAvatar(team.id, file),
    onSuccess: () => {
      toast.success('Team photo updated')
      qc.invalidateQueries({ queryKey: ['teams'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to upload photo'),
  })

  const deleteAvatarM = useMutation({
    mutationFn: () => api.teams.deleteAvatar(team.id),
    onSuccess: () => {
      toast.success('Team photo removed')
      qc.invalidateQueries({ queryKey: ['teams'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to remove photo'),
  })

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) uploadAvatarM.mutate(file)
    if (avatarInputRef.current) avatarInputRef.current.value = ''
  }

  const updateM = useMutation({
    // Always send `description` (even ''), not `undefined` — the
    // backend's sparse update treats an omitted key as "leave
    // unchanged," so clearing the field would otherwise silently
    // survive a "successful" save.
    mutationFn: () => api.teams.update(team.id, { name: editName.trim(), description: editDesc.trim(), customer_id: editCustomerId }),
    onSuccess: () => {
      toast.success('Team updated')
      qc.invalidateQueries({ queryKey: ['teams'] })
      setEditOpen(false)
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to update'),
  })

  const membersQ = useQuery({
    queryKey: ['team-members', team.id],
    queryFn: () => api.teams.listMembers(team.id),
  })

  const deleteM = useMutation({
    mutationFn: () => api.teams.remove(team.id),
    onSuccess: () => {
      toast.success('Team deleted')
      qc.invalidateQueries({ queryKey: ['teams'] })
      onBack()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  const removeMemberM = useMutation({
    mutationFn: (userId: string) => api.teams.removeMember(team.id, userId),
    onSuccess: () => {
      toast.success('Member removed')
      qc.invalidateQueries({ queryKey: ['team-members', team.id] })
      qc.invalidateQueries({ queryKey: ['teams'] })
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to remove'),
  })

  const members = membersQ.data ?? []

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <Box sx={{ px: 4, pt: 3, pb: 2.5, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <IconButton size="small" onClick={onBack} sx={{ color: 'text.secondary' }}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: 'text.secondary' }}>All teams</Typography>
        </Stack>

        <Stack direction="row" alignItems="flex-start" spacing={2}>
          {/* Team avatar — clickable to change */}
          <Tooltip title={can('teams:update') ? 'Change team photo' : ''} arrow>
            <Box
              onClick={can('teams:update') ? () => avatarInputRef.current?.click() : undefined}
              sx={{
                position: 'relative', width: 52, height: 52, borderRadius: '50%',
                overflow: 'hidden', flexShrink: 0,
                cursor: can('teams:update') ? 'pointer' : 'default',
                '&:hover .cam-overlay': { opacity: 1 },
              }}
            >
              {team.avatar_url ? (
                <Avatar src={team.avatar_url} sx={{ width: 52, height: 52 }} />
              ) : (
                <Box sx={{
                  width: 52, height: 52, borderRadius: '50%',
                  background: `linear-gradient(135deg, ${color}, ${color}BB)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <GroupsOutlinedIcon sx={{ fontSize: 24, color: '#fff' }} />
                </Box>
              )}
              {can('teams:update') && (
                <Box className="cam-overlay" sx={{
                  position: 'absolute', inset: 0,
                  bgcolor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 160ms ease',
                }}>
                  <CameraAltOutlinedIcon sx={{ fontSize: 18, color: '#fff' }} />
                </Box>
              )}
            </Box>
          </Tooltip>
          <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleAvatarChange} hidden />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
              {team.name}
            </Typography>
            {team.description && (
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5, lineHeight: 1.55 }}>
                {team.description}
              </Typography>
            )}
            <Box sx={{ mt: 0.75 }}>
              <CustomerScopeChip customerId={team.customer_id} requiredNullLabel="Auto (by members)" />
            </Box>
            {team.avatar_url && can('teams:update') && (
              <Button size="small" onClick={() => deleteAvatarM.mutate()}
                disabled={deleteAvatarM.isPending}
                sx={{ fontSize: 11, color: 'text.secondary', p: 0, mt: 0.25, minWidth: 0, '&:hover': { color: 'error.main' } }}>
                Remove photo
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={0.75} sx={{ flexShrink: 0 }}>
            <Tooltip title={can('teams:update') ? '' : 'No permission'} arrow>
              <span>
                <Button
                  variant="outlined" size="small"
                  disabled={!can('teams:update')}
                  onClick={() => { setEditName(team.name); setEditDesc(team.description ?? ''); setEditCustomerId(team.customer_id); setEditOpen(true) }}
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider' }}
                >
                  Edit
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={can('teams:delete') ? 'Delete team' : 'No permission'} arrow>
              <span>
                <IconButton
                  size="small"
                  disabled={!can('teams:delete')}
                  onClick={() => { confirmDlg({ title: `Delete "${team.name}"?`, message: "All team data will be removed.", confirmLabel: "Delete", variant: "danger" }).then((ok) => ok && deleteM.mutate()) }}
                  sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, '&:hover': { color: 'error.main' } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3,
          scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: 8 },
          '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
          '&:hover::-webkit-scrollbar-thumb': {
            background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)',
          },
        }}
      >
        <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
            Members
            <Typography component="span" sx={{ ml: 1, fontSize: 12, color: 'text.secondary', fontWeight: 500 }}>
              {members.length}
            </Typography>
          </Typography>
          <Tooltip title={can('teams:update') ? '' : 'No permission'} arrow>
            <span>
              <Button
                size="small"
                disabled={!can('teams:update')}
                startIcon={<PersonAddOutlinedIcon sx={{ fontSize: 14 }} />}
                onClick={() => setAddMembersOpen(true)}
                sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main' }}
              >
                Add members
              </Button>
            </span>
          </Tooltip>
        </Stack>

        {membersQ.isLoading ? (
          <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
        ) : (
          <Stack spacing={1}>
            {members.map((m, i) => {
              const initials = m.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()
              const isExpanded = expandedMemberId === m.id
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.26, ease: [0.32, 0.72, 0, 1] }}
                >
                  <Box
                    sx={{
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: isExpanded ? 'primary.main' : 'divider',
                      transition: 'border-color 120ms ease, box-shadow 120ms ease',
                      overflow: 'hidden',
                      '&:hover': {
                        borderColor: (t) => isExpanded ? t.palette.primary.main
                          : t.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.16)',
                      },
                    }}
                  >
                    {/* Clickable header row */}
                    <Stack
                      direction="row" alignItems="center" spacing={1.75}
                      onClick={() => setExpandedMemberId(isExpanded ? null : m.id)}
                      sx={{
                        px: 2, py: 1.5, cursor: 'pointer',
                        '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.02)' },
                      }}
                    >
                      <UserAvatar name={m.name} avatarUrl={m.avatar_url} size={36} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography sx={{ fontSize: 13.5, fontWeight: 600, letterSpacing: '-0.005em' }} noWrap>
                            {m.name}
                          </Typography>
                          {!m.is_active && (
                            <Chip label="Inactive" size="small" sx={{ height: 18, fontSize: 10, bgcolor: 'action.hover', color: 'text.secondary' }} />
                          )}
                          <Chip
                            icon={<BuildOutlinedIcon sx={{ fontSize: 11 }} />}
                            label={m.open_work_order_count > 0 ? `${m.open_work_order_count} open` : 'No open WOs'}
                            size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 600,
                              bgcolor: m.open_work_order_count > 0 ? '#D9770614' : (t) => t.palette.mode === 'light' ? '#F1F5F9' : 'rgba(255,255,255,0.06)',
                              color: m.open_work_order_count > 0 ? '#D97706' : 'text.secondary',
                              '& .MuiChip-icon': { color: m.open_work_order_count > 0 ? '#D97706' : 'text.secondary', ml: 0.5 }, '& .MuiChip-label': { px: 0.5 } }}
                          />
                        </Stack>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.2 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {m.role.replace(/_/g, ' ')}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>·</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>{m.email}</Typography>
                        </Stack>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Tooltip title={can('teams:update') ? 'Remove from team' : 'No permission'} arrow>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!can('teams:update')}
                              onClick={(e) => { e.stopPropagation(); confirmDlg({ title: `Remove ${m.name}?`, message: `They will lose access to ${team.name} but remain in the workspace.`, confirmLabel: "Remove", variant: "warning" }).then((ok) => ok && removeMemberM.mutate(m.id)) }}
                              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                            >
                              <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 18, color: 'text.secondary',
                            transition: 'transform 200ms ease',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </Stack>
                    </Stack>

                    {/* Expandable detail panel */}
                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.24, ease: [0.32, 0.72, 0, 1] }}
                          style={{ overflow: 'hidden' }}
                        >
                          <Divider />
                          <Box sx={{ px: 2, py: 2 }}>
                            {/* Profile header */}
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                              <UserAvatar name={m.name} avatarUrl={m.avatar_url} size={52} sx={{ bgcolor: color }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.01em' }}>
                                  {m.name}
                                </Typography>
                                <Chip
                                  label={m.role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                                  size="small"
                                  sx={{ height: 20, fontSize: 10.5, fontWeight: 700, mt: 0.25,
                                    bgcolor: `${color}18`, color, '& .MuiChip-label': { px: 1 } }}
                                />
                              </Box>
                            </Stack>

                            {/* Contact info */}
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
                              Contact
                            </Typography>
                            <Stack spacing={0.75} sx={{ mb: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1.25}>
                                <EmailOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>{m.email}</Typography>
                              </Stack>
                              {m.phone && (
                                <Stack direction="row" alignItems="center" spacing={1.25}>
                                  <PhoneOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                  <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>{m.phone}</Typography>
                                </Stack>
                              )}
                            </Stack>

                            {/* Activity */}
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
                              Activity
                            </Typography>
                            <Stack spacing={0.75} sx={{ mb: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1.25}>
                                <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 72 }}>Last active</Typography>
                                <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>
                                  {relativeTime(m.last_action_at ?? m.last_login_at, formatDateShort)}
                                </Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={1.25}>
                                <PersonOutlineIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 72 }}>Logins</Typography>
                                <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>{m.login_count}</Typography>
                              </Stack>
                              <Stack direction="row" alignItems="center" spacing={1.25}>
                                <AccessTimeOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography sx={{ fontSize: 12, color: 'text.secondary', minWidth: 72 }}>Joined team</Typography>
                                <Typography sx={{ fontSize: 12.5, color: 'text.primary' }}>
                                  {formatDate(m.added_at)}
                                </Typography>
                              </Stack>
                            </Stack>

                            {/* Work orders */}
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
                              Work orders
                            </Typography>
                            <Stack direction="row" spacing={1.5} sx={{ mb: 0.5 }}>
                              <Box sx={{ flex: 1, px: 1.5, py: 1.25, borderRadius: 1.25, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 18, fontWeight: 700, color: m.open_work_order_count > 0 ? '#D97706' : 'text.primary' }}>
                                  {m.open_work_order_count}
                                </Typography>
                                <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Open
                                </Typography>
                              </Box>
                              <Box sx={{ flex: 1, px: 1.5, py: 1.25, borderRadius: 1.25, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}>
                                  {m.total_work_order_count}
                                </Typography>
                                <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                  Total
                                </Typography>
                              </Box>
                            </Stack>

                            {/* Assigned work orders list */}
                            {m.open_work_order_count > 0 && (
                              <MemberWorkOrders userId={m.id} />
                            )}
                          </Box>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Box>
                </motion.div>
              )
            })}
            {members.length === 0 && (
              <Box sx={{ py: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>No members yet. Add people to this team.</Typography>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Edit team dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Edit team</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField label="Team name" value={editName} onChange={(e) => setEditName(e.target.value)}
              size="small" fullWidth autoFocus />
            <TextField label="Description (optional)" value={editDesc} onChange={(e) => setEditDesc(e.target.value)}
              size="small" fullWidth multiline minRows={2} />
            <CustomerScopePicker
              mode="catalog"
              value={editCustomerId}
              onChange={setEditCustomerId}
              label="Customer"
              helperText="Optional. Narrows this team to one customer on top of its members' own access."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" disabled={!editName.trim() || updateM.isPending} onClick={() => updateM.mutate()}>
            {updateM.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add members dialog */}
      <AddMembersDialog
        open={addMembersOpen}
        onClose={() => setAddMembersOpen(false)}
        team={team}
        existingMemberIds={members.map((m) => m.id)}
      />
    </Box>
  )
}

// ─── Add members dialog ─────────────────────────────────────────────────────

function AddMembersDialog({
  open, onClose, team, existingMemberIds,
}: {
  open: boolean
  onClose: () => void
  team: Team
  existingMemberIds: string[]
}) {
  const qc = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [search, setSearch] = useState('')

  const usersQ = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users.list(),
    enabled: open,
  })

  const existing = useMemo(() => new Set(existingMemberIds), [existingMemberIds])

  const available = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (usersQ.data ?? [])
      .filter((u) => u.is_active && !existing.has(u.id))
      .filter((u) => !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
  }, [usersQ.data, existing, search])

  const addM = useMutation({
    mutationFn: () => api.teams.addMembers(team.id, Array.from(selected)),
    onSuccess: () => {
      toast.success(`${selected.size} member${selected.size === 1 ? '' : 's'} added`)
      qc.invalidateQueries({ queryKey: ['team-members', team.id] })
      qc.invalidateQueries({ queryKey: ['teams'] })
      setSelected(new Set())
      setSearch('')
      onClose()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to add members'),
  })

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        Add members to {team.name}
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 0 }}>
        <TextField
          placeholder="Search people"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          autoFocus
          sx={{
            mb: 1.5,
            '& .MuiOutlinedInput-root': { fontSize: 13.5 },
          }}
        />

        <Box
          sx={{
            maxHeight: 320,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
          }}
        >
          {usersQ.isLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : available.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {search ? 'No matching people found.' : 'Everyone is already on this team.'}
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {available.map((u) => {
                const initials = u.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase()
                const checked = selected.has(u.id)
                return (
                  <ListItemButton
                    key={u.id}
                    onClick={() => toggle(u.id)}
                    sx={{
                      py: 1.25,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Checkbox
                      checked={checked}
                      size="small"
                      sx={{ mr: 1, p: 0 }}
                    />
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <UserAvatar name={u.name} avatarUrl={(u as any).avatar_url} size={32} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Stack direction="row" alignItems="center" spacing={0.75}>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, letterSpacing: '-0.005em' }} noWrap>
                            {u.name}
                          </Typography>
                          <Chip
                            label={formatRole(u.role)}
                            size="small"
                            sx={{
                              height: 17, fontSize: 9.5, fontWeight: 700, flexShrink: 0,
                              bgcolor: `${ROLE_COLORS[u.role] ?? '#64748B'}18`,
                              color: ROLE_COLORS[u.role] ?? '#64748B',
                              '& .MuiChip-label': { px: 0.65 },
                            }}
                          />
                        </Stack>
                      }
                      secondary={u.email}
                      secondaryTypographyProps={{ fontSize: 11.5 }}
                    />
                  </ListItemButton>
                )
              })}
            </List>
          )}
        </Box>

        {selected.size > 0 && (
          <Typography sx={{ mt: 1.5, fontSize: 12.5, fontWeight: 600, color: 'primary.main' }}>
            {selected.size} selected
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => addM.mutate()}
          disabled={selected.size === 0 || addM.isPending}
          sx={{ fontSize: 13, fontWeight: 600 }}
        >
          {addM.isPending ? 'Adding…' : `Add ${selected.size || ''} member${selected.size === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Create team dialog ─────────────────────────────────────────────────────

function CreateTeamDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [customerId, setCustomerId] = useState<string | null>(null)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  function handleAvatarPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarFile(file)
    setAvatarPreview(URL.createObjectURL(file))
  }

  function clearAvatar() {
    setAvatarFile(null)
    if (avatarPreview) URL.revokeObjectURL(avatarPreview)
    setAvatarPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const createM = useMutation({
    mutationFn: async () => {
      const team = await api.teams.create({
        name: name.trim(), description: description.trim() || undefined, customer_id: customerId,
      })
      if (avatarFile) {
        await api.teams.uploadAvatar(team.id, avatarFile)
      }
      return team
    },
    onSuccess: () => {
      toast.success('Team created')
      qc.invalidateQueries({ queryKey: ['teams'] })
      setName(''); setDescription(''); setCustomerId(null); clearAvatar()
      onClose()
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to create team'),
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Create a team</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 2.5 }}>
          Group people into crews so you can assign work orders to a whole team at once.
        </Typography>

        {/* Avatar picker */}
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.5 }}>
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              position: 'relative', width: 64, height: 64, borderRadius: '50%',
              bgcolor: 'action.hover', cursor: 'pointer', overflow: 'hidden',
              border: '2px dashed', borderColor: avatarPreview ? 'transparent' : 'divider',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'border-color 160ms ease',
              '&:hover': { borderColor: 'primary.main', '& .cam-overlay': { opacity: 1 } },
            }}
          >
            {avatarPreview ? (
              <Avatar src={avatarPreview} sx={{ width: 64, height: 64 }} />
            ) : (
              <CameraAltOutlinedIcon sx={{ fontSize: 22, color: 'text.disabled' }} />
            )}
            {avatarPreview && (
              <Box className="cam-overlay" sx={{
                position: 'absolute', inset: 0,
                bgcolor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: 0, transition: 'opacity 160ms ease',
              }}>
                <CameraAltOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
              </Box>
            )}
          </Box>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Team photo</Typography>
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>JPG, PNG or WebP. Max 5 MB.</Typography>
            {avatarPreview && (
              <Button size="small" onClick={clearAvatar}
                sx={{ fontSize: 11, color: 'error.main', mt: 0.25, p: 0, minWidth: 0 }}>
                Remove
              </Button>
            )}
          </Box>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleAvatarPick} hidden />
        </Stack>

        <TextField
          label="Team name" required
          value={name} onChange={(e) => setName(e.target.value)}
          size="small" fullWidth autoFocus
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { fontSize: 13.5 } }}
        />
        <TextField
          label="Description (optional)"
          value={description} onChange={(e) => setDescription(e.target.value)}
          size="small" fullWidth multiline minRows={2}
          sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { fontSize: 13.5 } }}
        />

        <CustomerScopePicker
          mode="catalog"
          value={customerId}
          onChange={setCustomerId}
          label="Customer"
          helperText="Optional. Narrows this team to one customer on top of its members' own access — leave as All to keep scope purely inferred from members, like before."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ fontSize: 13, color: 'text.secondary' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => createM.mutate()}
          disabled={!name.trim() || createM.isPending}
          sx={{ fontSize: 13, fontWeight: 600 }}
        >
          {createM.isPending ? 'Creating…' : 'Create team'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function relativeTime(iso: string | null, formatDateShort: (d: Date | string) => string): string {
  if (!iso) return 'Never'
  const diff = Date.now() - new Date(iso).getTime()
  if (diff < 60_000) return 'Just now'
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return formatDateShort(iso)
}

const PRIORITY_DOT: Record<string, string> = { low: '#22C55E', medium: '#F59E0B', high: '#F97316', critical: '#EF4444' }

function MemberWorkOrders({ userId }: { userId: string }) {
  const wosQ = useQuery({
    queryKey: ['member-wos', userId],
    queryFn: () => api.workOrders.list({ assigned_to: userId, status: 'open,in_progress' }),
    staleTime: 30_000,
  })
  const wos: any[] = Array.isArray(wosQ.data) ? wosQ.data.slice(0, 5) : []

  if (wosQ.isLoading) return <CircularProgress size={16} sx={{ mx: 'auto', display: 'block', my: 1 }} />
  if (wos.length === 0) return null

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 1 }}>
        Assigned work orders
      </Typography>
      <Stack spacing={0.5}>
        {wos.map((wo) => (
          <Stack key={wo.id} direction="row" alignItems="center" spacing={1.25}
            sx={{ px: 1.5, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider',
              '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.015)' : 'rgba(255,255,255,0.02)' } }}>
            <Box sx={{ width: 8, height: 8, borderRadius: 99, bgcolor: PRIORITY_DOT[wo.priority] ?? '#94A3B8', flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 500, flex: 1, minWidth: 0 }} noWrap>{wo.title}</Typography>
            <Chip label={wo.status?.replace('_', ' ')} size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 700, textTransform: 'capitalize',
                bgcolor: wo.status === 'in_progress' ? '#3B82F614' : '#F59E0B14',
                color: wo.status === 'in_progress' ? '#3B82F6' : '#F59E0B' }} />
          </Stack>
        ))}
      </Stack>
    </Box>
  )
}
